import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export default function Hero({ t, scrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const logoRotate = useTransform(scrollYProgress, [0, 1], [0, 6]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pb-16 pt-[130px] sm:pt-[150px]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 -top-40 h-[560px] w-[560px] rounded-full bg-gold/10 blur-[120px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-52 bottom-0 h-[420px] w-[420px] rounded-full bg-forest/5 blur-[100px]"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute right-6 top-1/2 hidden -translate-y-1/2 rotate-90 font-mono text-[10px] tracking-[0.4em] text-sand xl:block"
      >
        48.8837° N — 2.3119° E · PARIS 17E
      </span>

      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
            data-testid="hero-eyebrow"
            className="mb-7 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.28em] text-amberburn"
          >
            <span className="inline-block h-1.5 w-1.5 rotate-45 bg-gold" />
            {t.hero.eyebrow}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-forest sm:text-5xl lg:text-6xl"
          >
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${i === 2 ? "italic text-forestlight" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1.1, delay: 0.2 + i * 0.15, ease: EASE }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            data-testid="hero-subtext"
            className="mt-7 max-w-xl text-base leading-relaxed text-sand sm:text-lg"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-briefing"
              onClick={() => scrollTo("contact")}
              className="group flex items-center gap-2 rounded-full bg-forest px-7 py-3.5 text-sm font-semibold text-cream transition-all duration-300 hover:bg-forestlight hover:shadow-[0_12px_40px_-10px_rgba(13,40,24,0.5)]"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-intermediation"
              onClick={() => scrollTo("manifesto")}
              className="rounded-full border border-forest/25 px-7 py-3.5 text-sm font-semibold text-forest transition-all duration-300 hover:border-gold hover:text-forestlight"
            >
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-14 grid max-w-xl grid-cols-3 gap-6 border-t border-creamborder pt-7"
            data-testid="hero-metrics"
          >
            {t.hero.metrics.map((m, i) => (
              <div key={i}>
                <div className="font-serif text-2xl text-forest sm:text-3xl">
                  {m.value}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-sand">
                  {m.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: logoY, rotate: logoRotate }}
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
            className="relative"
            data-testid="hero-logo-frame"
          >
            <div
              aria-hidden
              className="absolute -inset-3 rounded-sm border border-gold/50"
            />
            <div className="slow-float relative overflow-hidden rounded-sm bg-creamdark/40 p-6 shadow-[0_30px_80px_-30px_rgba(13,40,24,0.35)]">
              <img
                src="/logo.jpeg"
                alt="Hindu Kush — banyan tree emblem"
                className="w-full mix-blend-multiply"
              />
            </div>
            <p className="mt-5 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-sand">
              {t.hero.logoCaption}
            </p>
          </motion.div>
        </div>
      </div>

      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        onClick={() => scrollTo("manifesto")}
        data-testid="hero-scroll-indicator"
        className="mx-auto mt-16 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-sand transition-colors hover:text-forest"
      >
        {t.hero.scroll}
        <ArrowDown className="h-3.5 w-3.5 animate-bounce" />
      </motion.button>
    </section>
  );
}
